import type { Measure, ObservedMetrics } from './contract'
import { MEASURES } from './contract'

/**
 * A ratio derived from two observed counts.
 *
 * `value` is `null` when the denominator is zero or missing, and the screen renders the
 * no-value glyph for it rather than a zero: a creative with no impressions has no CTR, and
 * "0.00%" would be a claim the data does not make.
 *
 * `partial` says the ratio was built on a measure only some in-scope ads reported. The
 * figure is real as far as it goes and is marked rather than hidden.
 */
export interface Ratio {
  value: number | null
  partial: boolean
}

export interface Derived {
  roas: Ratio
  cpa: Ratio
  aov: Ratio
  cpc: Ratio
  cpm: Ratio
  /** Percent, 0 to 100, as the benchmarks are. */
  ctr: Ratio
  hookRate: Ratio
  holdRate: Ratio
  /** `spend`, `conversions` or `conversionValue` is incomplete. */
  outcomePartial: boolean
  /** `impressions`, `clicks`, `threeSecondViews` or `thruPlays` is incomplete. */
  deliveryPartial: boolean
}

const OUTCOME: readonly Measure[] = ['spend', 'conversions', 'conversionValue']
const DELIVERY: readonly Measure[] = ['impressions', 'clicks', 'threeSecondViews', 'thruPlays']

/**
 * Every ratio the screen shows, from the counts the platform reported.
 *
 * One calculation read three times: the totals tiles, the table and the grid all
 * come through here, so they cannot drift from one another.
 */
export function derive(m: ObservedMetrics): Derived {
  const incomplete = new Set(m.incomplete)
  const ratio = (
    numerator: number | null,
    denominator: number | null,
    numeratorKey: Measure,
    denominatorKey: Measure,
    scale = 1,
  ): Ratio => ({
    value:
      numerator === null || denominator === null || denominator === 0
        ? null
        : (numerator / denominator) * scale,
    partial: incomplete.has(numeratorKey) || incomplete.has(denominatorKey),
  })

  return {
    roas: ratio(m.conversionValue, m.spend, 'conversionValue', 'spend'),
    cpa: ratio(m.spend, m.conversions, 'spend', 'conversions'),
    aov: ratio(m.conversionValue, m.conversions, 'conversionValue', 'conversions'),
    cpc: ratio(m.spend, m.clicks, 'spend', 'clicks'),
    cpm: ratio(m.spend, m.impressions, 'spend', 'impressions', 1000),
    ctr: ratio(m.clicks, m.impressions, 'clicks', 'impressions', 100),
    hookRate: ratio(m.threeSecondViews, m.impressions, 'threeSecondViews', 'impressions', 100),
    holdRate: ratio(m.thruPlays, m.threeSecondViews, 'thruPlays', 'threeSecondViews', 100),
    outcomePartial: OUTCOME.some((measure) => incomplete.has(measure)),
    deliveryPartial: DELIVERY.some((measure) => incomplete.has(measure)),
  }
}

/**
 * Whether the adjusted ROAS figure rests on a partial measure. The server shrinks the
 * creative's `conversionValue / spend` by its `conversions`, so the figure is partial
 * exactly when any outcome measure is: the raw ROAS ratio's two sides, or the count.
 */
export function adjustedPartial(row: { derived: Derived }): boolean {
  return row.derived.outcomePartial
}

/** The measures served as amounts in the account currency; the rest are counts. */
const MONEY: ReadonlySet<Measure> = new Set<Measure>(['spend', 'conversionValue'])

/**
 * Sum served amounts in whole cents and convert back once.
 *
 * The server holds integer cents and converts at serialization, so each served amount is
 * a two-decimal figure; adding them as binary fractions drifts (`0.10 + 0.70` is
 * `0.7999999999999999`), and a benchmark derived from the drifted sum can round a step
 * below the one the API serves (`docs/reference/creative-list-contract.md`, "Money").
 */
export function sumMoney(values: readonly number[]): number {
  return values.reduce((cents, value) => cents + Math.round(value * 100), 0) / 100
}

/**
 * Aggregate a list of observed metrics under the contract's null, zero and incomplete
 * rules - the same rules the server applies when it merges ads under a hash
 * (`docs/reference/creative-list-contract.md`, "Null, zero and incomplete").
 *
 * This is the one implementation the two screens share: `totalsOf` on the creatives screen
 * and `pool` on the hypotheses screen both call it, so a bucket and the totals row it is
 * checked against cannot disagree about what a missing measure means. The rules, from the
 * contract: a measure is the sum of the values reported to it; it is `null` when no input
 * reported it; and it is listed in the aggregate's `incomplete` when any input lists it, or
 * when some inputs reported it and the rest have it `null` - a `null` input means no in-scope
 * ad reported the measure, so ads that reported and ads that did not are both present, which
 * is the contract's own definition. Taking only the union of the inputs' lists would read
 * such a mix as complete, and a ranking surface would rank a partly unseen sample.
 */
export function aggregateObserved(list: ObservedMetrics[]): ObservedMetrics {
  return aggregateUnder(list, false)
}

/**
 * `aggregateObserved`, with every measure marked when the scope itself is partial (D8):
 * the sums stay what the rows reported, but none of them can claim the whole scope.
 */
export function aggregateUnder(list: ObservedMetrics[], scopePartial: boolean): ObservedMetrics {
  const sums = {} as Record<Measure, number | null>
  const incomplete = new Set<Measure>()
  for (const measure of MEASURES) {
    const reported = list
      .map((entry) => entry[measure])
      .filter((value): value is number => value !== null)
    sums[measure] =
      reported.length === 0
        ? null
        : MONEY.has(measure)
          ? sumMoney(reported)
          : reported.reduce((sum, value) => sum + value, 0)
    // Some reported it and some have it null, or an input said so itself: either way the
    // aggregate only covers part of what the scope delivered.
    if ((reported.length > 0 && reported.length < list.length) || list.some((entry) => entry.incomplete.includes(measure))) {
      incomplete.add(measure)
    }
  }
  return {
    spend: sums.spend,
    impressions: sums.impressions,
    clicks: sums.clicks,
    conversions: sums.conversions,
    conversionValue: sums.conversionValue,
    threeSecondViews: sums.threeSecondViews,
    thruPlays: sums.thruPlays,
    incomplete: MEASURES.filter((measure) => scopePartial || incomplete.has(measure)),
  }
}

/**
 * Integer spend shares over the segment, summing to 100.
 *
 * Largest remainder, because rounding each share on its own gives a column that reads 99%
 * or 101% and invites the reader to look for the missing creative. A creative that reported
 * no spend takes 0, and a segment with no spend at all takes zeroes rather than a division
 * by zero spread across every row.
 */
export interface SpendShare {
  /**
   * The integer percent of the set's reported spend, allocated so the column sums to
   * 100 over the reported rows. `null` when the row's spend is missing or the total is
   * zero: a share of an unknown or empty whole is unknown, not 0%.
   */
  pct: number | null
  /** The ratio rests on a partially reported spend, or against a total missing one of its parts. */
  partial: boolean
}

export function spendShares(rows: Array<{ metrics: ObservedMetrics }>): SpendShare[] {
  if (rows.length === 0) return []
  // The denominator is the set's spend, and it is only partly known when any member
  // under-reports or withholds its spend entirely - so every computable share carries
  // the mark, not just the row the gap sits in.
  const denominatorPartial =
    rows.some((row) => row.metrics.spend === null) ||
    rows.some((row) => row.metrics.incomplete.includes('spend'))
  const total = sumMoney(rows.map((row) => row.metrics.spend ?? 0))
  if (total <= 0) return rows.map(() => ({ pct: null, partial: denominatorPartial }))

  const exact = rows.map((row) => ((row.metrics.spend ?? 0) / total) * 100)
  const shares = exact.map((value) => Math.floor(value))
  let remaining = 100 - shares.reduce((sum, share) => sum + share, 0)

  // Hand the leftover points to the largest fractional parts, ties to the earlier row so
  // the same segment always allocates them the same way. A row with no reported spend
  // takes no share, however early its zero fraction sorts.
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((left, right) => right.fraction - left.fraction || left.index - right.index)

  for (const { index } of order) {
    if (remaining <= 0) break
    if (rows[index].metrics.spend === null) continue
    shares[index] += 1
    remaining -= 1
  }
  return rows.map((row, index) => ({
    pct: row.metrics.spend === null ? null : shares[index],
    partial: denominatorPartial,
  }))
}
